import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/shallow";
import { CustomForm } from "mobily-ui-library";
import { useGlobalStore } from "../stores/globalStore";
import { useCheckoutStore } from "../stores/checkoutStore";
import { useGetMainAndAroundODB } from "../hooks/useGetMainAndAroundODB";
import { useJourneyOrchestrator } from "../hooks/useJourneyOrchestrator";
import { CoverageError } from "./CoverageError";
import MobIcon from "../assets/MobIcon.svg";
import NonMobilyIcon from "../assets/NonMobilyIcon.svg";
import "./LocationForm.scss";

// Load Mobily Text v7 Light font and make it available for the google map infoWindow
import MobilyTextLightUrl from '../fonts/Mobily_Text_v6-Light.otf';
const preloadFont = (href: string, type = 'font/ttf') => {
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'font'
  link.type = type
  link.crossOrigin = 'anonymous'
  link.href = href
  document.head.appendChild(link)
}

preloadFont(MobilyTextLightUrl)

interface CustomMarkerProps {
    lat: number;
    lng: number;
    icon?: string;
    label?: string;
    onClick?: () => void;
    [key: string]: any;
}
interface OdbEntry {
    "odb-id": string;
    longitude: number;
    latitude: number;
    provider: string;
}
const buildMarkersFromOdbResponse = (data: {
    "main-mobily-odb"?: OdbEntry;
    "mobily-odbs"?: OdbEntry[];
    "non-mobily-odbs"?: OdbEntry[];
}): CustomMarkerProps[] => {
    const main = data?.["main-mobily-odb"];
    const mobilyOdbs = data?.["mobily-odbs"] || [];
    const nonMobilyOdbs = data?.["non-mobily-odbs"] || [];

    const allOdbs: OdbEntry[] = [
        ...(main?.["odb-id"] ? [main] : []),
        ...mobilyOdbs,
        ...nonMobilyOdbs,
    ];

    return allOdbs.map((odb) => ({
        lat: odb.latitude,
        lng: odb.longitude,
        label: odb["odb-id"],
        icon: odb.provider === "Mobily" ? MobIcon : NonMobilyIcon,
        provider: odb.provider,
    }));
};

export const LocationForm = () => {
    const { t } = useTranslation();
    const getMainAndAroundODB = useGetMainAndAroundODB();
    const handleStepSubmit = useJourneyOrchestrator();

    const [odbMarkers, setOdbMarkers] = useState<CustomMarkerProps[]>([]);
    const [selectedOdb, setSelectedOdb] = useState<CustomMarkerProps | null>(null);
    const [showNotification, setShowNotification] = useState(false);


    const { lang, coverageError, setCoverageError, locationState, setLocationState } = useGlobalStore(
        useShallow((state) => ({
            lang: state.lang,
            coverageError: state.coverageError,
            setCoverageError: state.setCoverageError,
            locationState: state.locationState,
            setLocationState: state.setLocationState,
        }))
    );
    const { capturedData, currentSection, currentStep } = useCheckoutStore(
        useShallow((state) => ({
            capturedData: state.capturedData,
            currentSection: state.currentSection,
            currentStep: state.currentStep,
        }))
    );
    const googleMapAPIKey = (window as any)["store_gmk"] || "";

    const handleGetMainAndAroundODB = async ({ lat, lng, range }: { lat: number, lng: number, range: number }) => {
        const res = await getMainAndAroundODB({ lat, lng, range });
        if (res.status === 200) {
            const markers = buildMarkersFromOdbResponse(res.data);
            setOdbMarkers(markers);
            setShowNotification(true);
        }
    };
    const handleLocationTransform = (data: {
        location: { lat: number; lng: number };
        address: string;
        components: any[];
        raw: any;
    }) => {
        handleGetMainAndAroundODB({
            lat: data.location.lat,
            lng: data.location.lng,
            range: 100,
        })
        setLocationState({
            coords: data.location,
            address: data.address,
        });

        return [
            {
                name: "location",
                value: {
                    coords: data.location,
                    address: data.address,
                },
            },
        ]
    };
    const handleNotificationAction = () => {
        setCoverageError(true);
    }
    const onSubmit = async () => {
        const existingSection = capturedData.find((s) => s.sectionId === currentSection?.sectionId);
        const newStep = {
            sequence: currentStep?.sequence,
            stepId: currentStep?.stepId,
            offerContainerId: currentStep?.offerContainerId,
            fields: [
                {
                    name: "Customer Selected ODB ID",
                    value: selectedOdb?.label ?? "",
                }
            ],
        };
        const locationSection = {
            sectionId: currentSection?.sectionId,
            steps: [
                ...(existingSection?.steps ?? []).filter((s: any) => s.stepId !== currentStep?.stepId),
                newStep,
            ],
        };

        await handleStepSubmit({ sectionData: locationSection });
    }

    const rtl = lang === 'ar';
    return (
        <>
            <CustomForm
                title={t("select_Location")}
                fields={[
                    {
                        id: "location",
                        name: "location",
                        fieldType: "map",
                        headingleft: t("location_Details"),
                        placeholder: t("enter_Location_Details"),
                        type: "text",
                        dropdownContainerClassName: "customDropdownClassName",
                        mapContainerClassName: "customMapContainerClassName",
                        rtl,
                        colSpanSm: 24,
                        colSpanMd: 24,
                        colSpanLg: 24,
                        mapKey: googleMapAPIKey,
                        rules: [{ required: true, message: t("location_Details_Error") }],
                        customMarkers: odbMarkers,
                        notificationMessage: showNotification && (<div className="locationForm__notification-content">
                            <span className="locationForm__notification-question">{t("map_notification_text")}</span>
                            <span className="locationForm__notification-clickHere" onClick={handleNotificationAction}>
                                {t("map_notification_click_here")}
                            </span>
                        </div>),
                        renderMarkerPopup: (marker: CustomMarkerProps) => {
                            const isMobily = marker.provider === "Mobily" || marker.provider === "MOB";
                            return `
                                <div class="odb-popup">
                                    <p class="odb-popup__row">
                                        <span class="odb-popup__key">${t("odb_popup_operator")}:</span>
                                        <span class="odb-popup__value">${isMobily ? "MOBILY" : marker.provider}</span>
                                    </p>
                                    <p class="odb-popup__row">
                                        <span class="odb-popup__key">${t("odb_popup_ODB")}:</span>
                                        <span class="odb-popup__value">${marker.label}</span>
                                    </p>
                                    <button 
                                        class="odb-popup__btn" 
                                        id="map-popup-btn-${marker.index}"
                                    >
                                        ${t("odb_popup_yes_this_is_my_odb")}
                                    </button>
                                </div>
                            `;
                        },
                        onMarkerPopupAction: (marker: CustomMarkerProps) => {
                            setSelectedOdb(marker);
                        }
                    },
                ]}
                footerFields={[
                    {
                        id: "submitButton",
                        name: "submitButton",
                        fieldType: "button",
                        primary: true,
                        variant: selectedOdb ? 'default' : 'disabled',
                        children: t("confirmBtn"),
                        htmlType: 'submit',
                        rtl,
                        formFieldClassName: "nextButton",
                    }
                ]}
                footerProps={{
                    justify: "center",
                    align: "middle",
                }}
                gutter={[28, 28]}
                rtl={rtl}
                initialValues={{
                    location : locationState
                }}
                transformLocationDetails={handleLocationTransform}
                onFinish={onSubmit}
                rootClassName={"locationForm"}
            />
            {coverageError && <CoverageError />}
        </>
    );
}